/**
 * Déclarer un équipement — le parcours le plus important du produit
 *
 * Trois étapes, et la troisième est le moment de vérité : l'application montre ce
 * qu'elle va programmer, avec l'origine de chaque échéance, et l'utilisateur
 * décoche ce qui ne le concerne pas **avant** que ça entre dans son agenda.
 *
 * Deux détails qui comptent :
 *
 *  - « je ne sais pas » est une réponse légitime pour le dernier entretien.
 *    L'application le dit alors (« estimée, à ajuster ») au lieu d'inventer une
 *    date crédible ;
 *  - la simulation appelle la même route que la création, en lecture seule. Ce
 *    qui s'affiche est donc exactement ce qui sera créé.
 */

import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, Search, Sparkles } from 'lucide-react';

import type { Caracteristique, RegleDeduite, TypeEquipement } from '@foyer/noyau';
import { LIBELLE_CATEGORIE, aujourdhuiParis } from '@foyer/noyau';

import { BadgeOrigine, Champ, Chargement, Erreur, TRAIT, echeanceEnClair } from '../composants/ui.js';
import { useAppli, useRessource, navigue } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';

type Valeurs = Record<string, string>;

export function AssistantEquipement() {
  const { rafraichis } = useAppli();
  const catalogues = useRessource(() => donnees.catalogues());
  const logements = useRessource(() => donnees.logements());

  const [etape, setEtape] = useState(1);
  const [recherche, setRecherche] = useState('');
  const [typeCle, setTypeCle] = useState<string | null>(null);
  const [nom, setNom] = useState('');
  const [piece, setPiece] = useState('');
  const [dateAchat, setDateAchat] = useState('');
  const [dateInstallation, setDateInstallation] = useState('');
  const [dernierEntretien, setDernierEntretien] = useState('');
  const [entretienInconnu, setEntretienInconnu] = useState(false);
  const [valeurs, setValeurs] = useState<Valeurs>({});
  const [simulation, setSimulation] = useState<{ regles: RegleDeduite[]; avertissements: string[] } | null>(null);
  const [retenues, setRetenues] = useState<Set<string>>(new Set());
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const types = catalogues.donnee?.equipements.types ?? [];
  const type = useMemo(() => types.find((t) => t.cle === typeCle) ?? null, [types, typeCle]);

  const familles = useMemo(() => {
    const filtre = recherche.trim().toLowerCase();
    const retenus = filtre
      ? types.filter((t) => t.nom.toLowerCase().includes(filtre) || t.famille.toLowerCase().includes(filtre))
      : types;
    const groupes = new Map<string, TypeEquipement[]>();
    for (const t of retenus) groupes.set(t.famille, [...(groupes.get(t.famille) ?? []), t]);
    return [...groupes.entries()];
  }, [types, recherche]);

  const caracteristiques = useMemo(() => {
    const sortie: Record<string, string | number | boolean> = {};
    for (const [cle, brut] of Object.entries(valeurs)) {
      if (brut === '') continue;
      const definition = type?.caracteristiques.find((c) => c.cle === cle);
      if (!definition) continue;
      sortie[cle] =
        definition.type === 'nombre' ? Number(brut) : definition.type === 'booleen' ? brut === 'true' : brut;
    }
    return sortie;
  }, [valeurs, type]);

  // L'étape 3 interroge la simulation : aucune écriture avant validation.
  useEffect(() => {
    if (etape !== 3 || !typeCle) return;
    let vivant = true;
    setErreur(null);
    donnees
      .simuleEquipement({
        typeCle,
        dateAchat: dateAchat || null,
        dateInstallation: dateInstallation || null,
        dernierEntretien: entretienInconnu ? null : dernierEntretien || null,
        caracteristiques,
      })
      .then((s) => {
        if (!vivant) return;
        setSimulation(s);
        setRetenues(new Set(s.regles.filter((r) => r.active).map((r) => r.modeleCle)));
      })
      .catch((err: Error) => vivant && setErreur(err.message));
    return () => {
      vivant = false;
    };
  }, [etape, typeCle, dateAchat, dateInstallation, dernierEntretien, entretienInconnu, caracteristiques]);

  async function valide() {
    if (!typeCle) return;
    setOccupe(true);
    setErreur(null);
    try {
      const r = await donnees.creeEquipement({
        typeCle,
        nom: nom || type?.nom || 'Équipement',
        piece: piece || null,
        dateAchat: dateAchat || null,
        dateInstallation: dateInstallation || null,
        dernierEntretien: entretienInconnu ? null : dernierEntretien || null,
        dernierEntretienInconnu: entretienInconnu,
        caracteristiques,
        logementId: logements.donnee?.logements[0]?.id ?? null,
        retenues: [...retenues],
      });
      rafraichis();
      navigue(`/maison/${r.equipement.id}`);
    } catch (err) {
      setErreur((err as Error).message);
      setOccupe(false);
    }
  }

  if (catalogues.chargement && !catalogues.donnee) return <Chargement quoi="Chargement du catalogue" />;
  if (catalogues.erreur && !catalogues.donnee) return <Erreur message={catalogues.erreur} />;

  return (
    <>
      <div className="entete-vue">
        <h1>
          Déclarer un équipement
        </h1>
        <button type="button" className="bouton texte" onClick={() => navigue('/maison')}>
          <ArrowLeft strokeWidth={TRAIT} size={16} aria-hidden />
          Retour
        </button>
      </div>

      <div className="etapes" aria-label={`Étape ${etape} sur 3`}>
        {[1, 2, 3].map((n) => (
          <i key={n} className={n <= etape ? 'faite' : ''} />
        ))}
      </div>

      {/* ── Étape 1 : le type ────────────────────────────────────────────── */}
      {etape === 1 && (
        <>
          <p className="chapeau">
            Choisissez le type : c’est lui qui porte les périodicités et les obligations. « Autre
            équipement » reste entièrement libre.
          </p>
          <Champ etiquette="Rechercher">
            <div style={{ position: 'relative' }}>
              <Search
                strokeWidth={TRAIT}
                size={18}
                aria-hidden
                style={{ position: 'absolute', left: 13, top: 13, color: 'var(--encre-3)' }}
              />
              <input
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="pompe à chaleur, fosse, véhicule…"
                style={{ paddingLeft: 40 }}
              />
            </div>
          </Champ>

          {familles.map(([famille, liste]) => (
            <section key={famille}>
              <div className="section-titre" style={{ cursor: 'default' }}>
                {famille}
              </div>
              <div className="grille-cartes deux">
                {liste.map((t) => (
                  <button
                    key={t.cle}
                    type="button"
                    className="carte"
                    style={{
                      textAlign: 'left',
                      cursor: 'pointer',
                      font: 'inherit',
                      color: 'inherit',
                      borderColor: typeCle === t.cle ? 'var(--accent)' : undefined,
                      margin: 0,
                    }}
                    onClick={() => {
                      setTypeCle(t.cle);
                      setNom(t.nom);
                      setValeurs({});
                      setEtape(2);
                    }}
                  >
                    <strong>{t.nom}</strong>
                    <div className="sourdine">
                      {LIBELLE_CATEGORIE[t.categorie]}
                      {t.libre ? ' · type libre' : ` · ${t.regles.length} règle(s)`}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {/* ── Étape 2 : les dates ──────────────────────────────────────────── */}
      {etape === 2 && type && (
        <>
          <p className="chapeau">
            Les dates servent de point de départ aux calculs. Si vous ne connaissez pas le dernier
            entretien, dites-le : l’application préférera annoncer une échéance estimée plutôt
            qu’inventer une date.
          </p>

          <div className="carte">
            <div className="rangee">
              <Champ etiquette="Nom">
                <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder={type.nom} />
              </Champ>
              <Champ etiquette="Pièce ou emplacement">
                <input value={piece} onChange={(e) => setPiece(e.target.value)} placeholder="Garage" />
              </Champ>
            </div>

            <div className="rangee">
              <Champ etiquette="Date d’achat">
                <input type="date" value={dateAchat} onChange={(e) => setDateAchat(e.target.value)} />
              </Champ>
              <Champ etiquette="Date d’installation">
                <input
                  type="date"
                  value={dateInstallation}
                  onChange={(e) => setDateInstallation(e.target.value)}
                />
              </Champ>
            </div>

            <Champ etiquette="Dernier entretien connu">
              <input
                type="date"
                value={dernierEntretien}
                disabled={entretienInconnu}
                onChange={(e) => setDernierEntretien(e.target.value)}
              />
            </Champ>
            <label className="ligne-reglage" style={{ borderBottom: 0, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={entretienInconnu}
                onChange={(e) => setEntretienInconnu(e.target.checked)}
                style={{ width: 22, height: 22, minHeight: 0, flex: '0 0 auto' }}
              />
              <span className="texte">Je ne sais pas</span>
            </label>

            {type.caracteristiques.length > 0 && (
              <div className="rangee" style={{ marginTop: 6 }}>
                {type.caracteristiques.map((c) => (
                  <ChampCaracteristique
                    key={c.cle}
                    caracteristique={c}
                    valeur={valeurs[c.cle] ?? ''}
                    onChange={(v) => setValeurs((p) => ({ ...p, [c.cle]: v }))}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="actions">
            <button type="button" className="bouton" onClick={() => setEtape(3)}>
              Voir ce qui sera programmé
            </button>
            <button type="button" className="bouton secondaire" onClick={() => setEtape(1)}>
              Changer de type
            </button>
          </div>
        </>
      )}

      {/* ── Étape 3 : le moment de vérité ────────────────────────────────── */}
      {etape === 3 && (
        <>
          <p className="chapeau">
            Voilà ce que l’application va programmer. Décochez ce qui ne vous concerne pas : rien
            n’est enregistré avant que vous validiez.
          </p>

          {erreur && <Erreur message={erreur} />}
          {!simulation && !erreur && <Chargement quoi="Calcul des échéances" />}

          {simulation?.regles.length === 0 && (
            <div className="note">
              Aucune échéance déduite pour ce type : les tâches se saisiront à la main.
            </div>
          )}

          {simulation?.regles.map((r) => {
            const coche = retenues.has(r.modeleCle);
            return (
              <label
                key={r.modeleCle}
                className="tache"
                style={{
                  ['--liseré' as string]: `var(--cat-${r.categorie})`,
                  cursor: 'pointer',
                  opacity: r.passe ? 0.6 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={coche}
                  onChange={() =>
                    setRetenues((p) => {
                      const s = new Set(p);
                      if (s.has(r.modeleCle)) s.delete(r.modeleCle);
                      else s.add(r.modeleCle);
                      return s;
                    })
                  }
                  style={{ width: 24, height: 24, minHeight: 0, flex: '0 0 auto', accentColor: 'var(--accent)' }}
                />
                <div className="corps">
                  <div className="titre">{r.titre}</div>
                  <div className="meta">
                    {r.description} · prochaine le {echeanceEnClair(r.echeance!, aujourdhuiParis())}
                    {r.rappels.length > 0 && ` · rappel à J-${r.rappels.join(', J-')}`}
                  </div>
                  <span className="badges">
                    <BadgeOrigine origine={r.origine} libelle={r.libelleOrigine} />
                    {r.reference && <span className="badge reference">{r.reference}</span>}
                    {r.echeanceEstimee && <span className="badge attention">estimée, à ajuster</span>}
                    {r.conditionIndeterminee && (
                      <span className="badge attention">
                        à confirmer : {(r.manquantesLibelles.length ? r.manquantesLibelles : r.manquantes).join(', ')}
                      </span>
                    )}
                    {r.documentAttendu && <span className="badge">📄 {r.documentAttendu}</span>}
                    {r.passe && <span className="badge">déjà passée</span>}
                  </span>
                </div>
              </label>
            );
          })}

          {simulation && simulation.avertissements.length > 0 && (
            <div className="note">
              <strong>Ce que l’application ne sait pas :</strong>
              <ul>
                {simulation.avertissements.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="actions">
            {/* Bouton d'engagement : c'est l'acte qui programme des rappels. */}
            <button
              type="button"
              className="bouton engagement"
              onClick={() => void valide()}
              disabled={occupe || !simulation}
            >
              <Check strokeWidth={TRAIT} size={18} aria-hidden />
              {occupe ? 'Enregistrement…' : `Programmer ${retenues.size} échéance(s)`}
            </button>
            <button type="button" className="bouton secondaire" onClick={() => setEtape(2)}>
              Corriger les dates
            </button>
          </div>

          <p className="sourdine" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkles strokeWidth={TRAIT} size={14} aria-hidden />
            Vous pourrez modifier ou supprimer chacune de ces échéances ensuite : vos réglages ne
            seront pas écrasés.
          </p>
        </>
      )}
    </>
  );
}

function ChampCaracteristique({
  caracteristique,
  valeur,
  onChange,
}: {
  caracteristique: Caracteristique;
  valeur: string;
  onChange(v: string): void;
}) {
  const etiquette = caracteristique.unite
    ? `${caracteristique.libelle}`
    : caracteristique.libelle;
  return (
    <Champ etiquette={etiquette} aide={caracteristique.aide}>
      {caracteristique.type === 'choix' ? (
        <select value={valeur} onChange={(e) => onChange(e.target.value)}>
          <option value="">non précisé</option>
          {caracteristique.choix?.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={caracteristique.type === 'nombre' ? 'number' : 'text'}
          value={valeur}
          placeholder={
            caracteristique.defaut !== undefined ? `par défaut ${String(caracteristique.defaut)}` : 'non précisé'
          }
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Champ>
  );
}
